/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import TransactionalMessageModel, { TransactionalMessageChannelType } from 'cio/models/transactional-message';
import codeSnippets, {
  replaceSnippetVariableWithHTMLTag,
  TransactionalCodeSnippetVars,
} from 'cio/utils/transactional/code-snippets';
import { findGroupsInSnippet } from 'cio/utils/transactional/find-groups-in-snippet';
import { TransactionalCodeSnippetLanguage } from 'cio/utils/transactional/code-snippet-request-renderer';
import EnvironmentService from 'cio/services/environment';
import AccountService from 'cio/services/account';
import hljs from 'cio/utils/highlightjs';
import { Editor } from 'cio/services/editor';
import { isCustomPushPayloadEmpty } from 'cio/utils/push/custom-payload';
import { isEmpty } from '@ember/utils';

interface TransactionalCodeSnippetsArgs {
  message: TransactionalMessageModel;
  highlightedGroup?: number;
  activeSnippetName: TransactionalCodeSnippetLanguage;
  onUpdateActiveSnippet: (name: TransactionalCodeSnippetLanguage) => unknown;
  onCopy: () => unknown;
}

export default class TransactionalCodeSnippets extends Component<TransactionalCodeSnippetsArgs> {
  @service environment!: EnvironmentService;
  @service account!: AccountService;

  @tracked highlightedLinesStart = 0;
  @tracked highlightedLinesCount = 0;

  @computed(
    'args.message.template.{vars.[],subject,body,bodyJson,fromIdentity.address}',
    'args.message.{type,triggerName}',
    'environment.current.identifierType',
  )
  get snippets() {
    return codeSnippets[this.args.message.type].map((snippet) => {
      let rawContent = snippet.content(
        this.getCodeSnippetVars(this.args.message.type),
        this.environment.current.identifierType,
      );
      let highlightedContent = hljs.highlight(rawContent, { language: snippet.language }).value;
      let content = replaceSnippetVariableWithHTMLTag(highlightedContent);

      return {
        ...snippet,
        content,
        groups: findGroupsInSnippet(content),
      };
    });
  }

  @action
  updateActiveSnippet(name: TransactionalCodeSnippetLanguage) {
    this.args.onUpdateActiveSnippet(name);

    // If there is a highlighted group, reset it.
    if (this.args.highlightedGroup !== undefined) {
      this.highlightGroup(0);
    }
  }

  @action
  highlightGroup(n: number) {
    let snippet = this.snippets.find((snippet) => snippet.name === this.args.activeSnippetName);
    if (!snippet || n === undefined) {
      return;
    }

    let group = snippet.groups[n];

    this.highlightedLinesStart = group[0];
    this.highlightedLinesCount = group[1] - group[0];
  }

  private getCodeSnippetVars(channelType: TransactionalMessageChannelType) {
    let { isContentSourceTemplate, template } = this.args.message;
    let vars: TransactionalCodeSnippetVars = {
      editor: template.get('editor'),
      transactionalMessageId: this.getMessageTriggerName(),
      messageVariables: template.get('variables') ?? [],
      context: {
        dataCenter: this.account.current.dataCenter,
      },
    };

    switch (channelType) {
      case TransactionalMessageChannelType.Email: {
        if (isContentSourceTemplate) {
          let from = template.get('fromIdentity')?.get('address');

          vars.from = from ? escape(from) : undefined;
          vars.subject = template.get('subject');
          vars.body = template.get('body');
        }
        break;
      }
      case TransactionalMessageChannelType.Push: {
        if (vars.editor === Editor.JSON) {
          vars.custom_payload_empty = isCustomPushPayloadEmpty(template);
        } else {
          vars.title = template.get('subject');
          vars.message = template.get('body');
        }
      }
    }

    return vars;
  }

  private getMessageTriggerName() {
    return isEmpty(this.args.message.triggerName) ? this.args.message.id : this.args.message.triggerName;
  }
}
